import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Empty, Input, Modal, Select, Spin, Table, Tabs, Tag, Typography, message } from 'antd'
import { CheckCircleOutlined, RobotOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import {
  confirmAicReliabilityRule,
  createAicReliabilityRule,
  generateAicReliabilityRule,
  getAicDashboardDatasetDetail,
  getAicReliabilityRules
} from '../../request'
import { buildDatasetTableOptions, display, formatDateTime, isCandidateSavable, ruleStatusMeta, unwrapList } from './aicDashboardUtils'

const RULE_TYPES = ['delay', 'coverage', 'quality']

const RULE_LABELS = {
  delay: '导入截止规则',
  coverage: '数据截止日期',
  quality: '字段质量规则'
}

const initialDrafts = {
  delay: {
    description: '距离上次成功上传超过一个更新周期时，判定为数据断更',
    timezone: 'Asia/Shanghai',
    calendar: 'natural',
    gracePeriodHours: 0
  },
  coverage: { tableId: undefined, fieldName: undefined, granularity: 'day', description: '' },
  quality: { tableId: undefined, fieldName: undefined, severity: 'warning', description: '' }
}

const cloneDrafts = () => ({
  delay: { ...initialDrafts.delay },
  coverage: { ...initialDrafts.coverage },
  quality: { ...initialDrafts.quality }
})

const errorMessage = (error, fallback) => (
  (error && error.response && error.response.data && (error.response.data.msg || error.response.data.message)) ||
  (error && (error.msg || error.message)) || fallback
)

function RuleStatus ({ value }) {
  const meta = ruleStatusMeta(value)
  return <Tag color={meta.color}>{meta.label}</Tag>
}

function CandidatePreview ({ candidate }) {
  if (!candidate) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="填写规则说明后生成 AI 候选方案" />
  }
  const risks = Array.isArray(candidate.risks) ? candidate.risks : []
  const ambiguities = Array.isArray(candidate.ambiguities) ? candidate.ambiguities : []
  const savable = isCandidateSavable(candidate)
  return <div className="aic-candidate-preview">
    <Alert
      showIcon
      type={savable ? 'success' : 'warning'}
      message={savable ? '服务端静态安全检查通过，等待人工确认' : '候选规则暂不可用'}
      description={display(candidate.explanation, '服务端未返回说明')}
    />
    {(ambiguities.length > 0 || risks.length > 0) && <div className="aic-candidate-notes">
      {ambiguities.length > 0 && <div><strong>需要澄清</strong><ul>{ambiguities.map((item, index) => <li key={`ambiguity-${index}`}>{item}</li>)}</ul></div>}
      {risks.length > 0 && <div><strong>风险提示</strong><ul>{risks.map((item, index) => <li key={`risk-${index}`}>{item}</li>)}</ul></div>}
    </div>}
    <div className="aic-code-block">
      <div className="aic-code-block-title">候选 SQL</div>
      <pre>{candidate.generatedSql || '未生成 SQL，不能保存或启用该规则'}</pre>
    </div>
    {candidate.normalizedRuleJson && <div className="aic-code-block">
      <div className="aic-code-block-title">结构化规则</div>
      <pre>{candidate.normalizedRuleJson}</pre>
    </div>}
  </div>
}

export default function AicReliabilitySettings ({ datasets, Nav }) {
  const location = useLocation()
  const queryDatasetId = useMemo(() => new URLSearchParams(location.search).get('datasetId'), [location.search])
  const [datasetId, setDatasetId] = useState(undefined)
  const [detail, setDetail] = useState(null)
  const [activeType, setActiveType] = useState('coverage')
  const [rules, setRules] = useState({ delay: [], coverage: [], quality: [] })
  const [drafts, setDrafts] = useState(cloneDrafts)
  const [candidate, setCandidate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const selectedDataset = useMemo(
    () => datasets.find(item => String(item.datasetId) === String(datasetId)),
    [datasets, datasetId]
  )
  const fields = detail && Array.isArray(detail.fields) ? detail.fields : []
  const tables = useMemo(() => buildDatasetTableOptions(detail), [detail])
  const activeDraft = drafts[activeType]
  const activeFields = activeType === 'delay' || !activeDraft.tableId
    ? []
    : fields.filter(field => String(field.tableId) === String(activeDraft.tableId))

  const loadRules = useCallback(async (selectedId, projectId) => {
    setLoading(true)
    setError('')
    setCandidate(null)
    try {
      const [datasetDetail, delay, coverage, quality] = await Promise.all([
        getAicDashboardDatasetDetail(projectId),
        getAicReliabilityRules('delay', selectedId),
        getAicReliabilityRules('coverage', selectedId),
        getAicReliabilityRules('quality', selectedId)
      ])
      setDetail(datasetDetail || null)
      setRules({ delay: unwrapList(delay), coverage: unwrapList(coverage), quality: unwrapList(quality) })
    } catch (requestError) {
      setDetail(null)
      setRules({ delay: [], coverage: [], quality: [] })
      setError(errorMessage(requestError, '规则配置加载失败，请稍后重试'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (datasetId || datasets.length === 0) return
    const requested = datasets.find(item => String(item.datasetId) === String(queryDatasetId))
    setDatasetId((requested || datasets[0]).datasetId)
  }, [datasets, datasetId, queryDatasetId])

  useEffect(() => {
    if (!datasetId) return
    const dataset = datasets.find(item => String(item.datasetId) === String(datasetId))
    if (dataset && dataset.projectId) loadRules(datasetId, dataset.projectId)
  }, [datasetId, datasets, loadRules])

  useEffect(() => {
    if (activeType === 'delay' || tables.length === 0 || activeDraft.tableId) return
    setDrafts(current => ({
      ...current,
      [activeType]: { ...current[activeType], tableId: tables[0].value, fieldName: undefined }
    }))
  }, [activeType, activeDraft.tableId, tables])

  const updateDraft = patch => {
    setCandidate(null)
    setDrafts(current => ({ ...current, [activeType]: { ...current[activeType], ...patch } }))
  }

  const generateCandidate = async () => {
    const draft = drafts[activeType]
    if (!draft.tableId || !draft.fieldName || !draft.description.trim()) {
      message.warning('请选择逻辑表和字段，并填写规则说明')
      return
    }
    setGenerating(true)
    setCandidate(null)
    try {
      const body = activeType === 'coverage'
        ? { tableId: draft.tableId, dateField: draft.fieldName, description: draft.description.trim() }
        : { tableId: draft.tableId, fieldName: draft.fieldName, description: draft.description.trim() }
      const result = await generateAicReliabilityRule(datasetId, activeType, body)
      setCandidate(result || null)
      if (!isCandidateSavable(result)) message.warning('服务端未返回通过静态安全检查的候选 SQL')
    } catch (requestError) {
      message.error(errorMessage(requestError, 'AI 候选生成失败'))
    } finally {
      setGenerating(false)
    }
  }

  const saveDraft = async () => {
    const draft = drafts[activeType]
    if (!selectedDataset || !selectedDataset.projectId) {
      message.error('数据源信息不完整，请刷新后重试')
      return
    }
    let body
    if (activeType === 'delay') {
      if (!draft.description.trim()) {
        message.warning('请填写导入截止规则')
        return
      }
      body = {
        datasetId,
        naturalLanguageRule: draft.description.trim(),
        normalizedRuleJson: JSON.stringify({
          strategy: 'last_success_over_expected_interval',
          timezone: draft.timezone,
          calendar: draft.calendar,
          gracePeriodHours: Number(draft.gracePeriodHours) || 0
        }),
        timezone: draft.timezone,
        calendar: draft.calendar,
        gracePeriodHours: Number(draft.gracePeriodHours) || 0
      }
    } else {
      if (!isCandidateSavable(candidate)) {
        message.warning('请先生成通过服务端安全检查的候选 SQL')
        return
      }
      body = activeType === 'coverage'
        ? {
            datasetId,
            tableId: draft.tableId,
            dateField: draft.fieldName,
            granularity: draft.granularity,
            filterJson: '{}',
            naturalLanguageRule: draft.description.trim(),
            generatedSql: candidate.generatedSql,
            normalizedSql: candidate.generatedSql,
            validationStatus: 'passed',
            validationMessage: candidate.explanation
          }
        : {
            datasetId,
            tableId: draft.tableId,
            fieldName: draft.fieldName,
            description: draft.description.trim(),
            ruleDslJson: candidate.normalizedRuleJson || '{}',
            generatedSql: candidate.generatedSql,
            normalizedSql: candidate.generatedSql,
            severity: draft.severity,
            validationStatus: 'passed',
            validationMessage: candidate.explanation
          }
    }
    setSaving(true)
    try {
      await createAicReliabilityRule(activeType, body)
      message.success('规则草稿已保存，尚未启用')
      setCandidate(null)
      await loadRules(datasetId, selectedDataset.projectId)
    } catch (requestError) {
      message.error(errorMessage(requestError, '规则草稿保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const confirmRule = rule => {
    if (!selectedDataset || !selectedDataset.projectId) {
      message.error('数据源信息不完整，请刷新后重试')
      return
    }
    Modal.confirm({
      title: `确认启用${RULE_LABELS[activeType]} v${rule.version}？`,
      content: '确认后该版本不可再编辑。此操作只启用规则，不会立即执行规则 SQL。',
      okText: '确认启用',
      cancelText: '取消',
      onOk: async () => {
        try {
          await confirmAicReliabilityRule(activeType, rule.id)
          message.success('规则已启用')
          await loadRules(datasetId, selectedDataset.projectId)
        } catch (requestError) {
          message.error(errorMessage(requestError, '规则确认失败'))
          throw requestError
        }
      }
    })
  }

  const ruleColumns = [
    { title: '版本', dataIndex: 'version', width: 82, render: value => `v${display(value)}` },
    { title: '状态', dataIndex: 'status', width: 100, render: value => <RuleStatus value={value} /> },
    {
      title: '规则内容',
      render: (_, rule) => display(rule.naturalLanguageRule || rule.description)
    },
    { title: '安全校验', dataIndex: 'validationStatus', width: 110, render: value => value ? <Badge status={value === 'passed' ? 'success' : 'warning'} text={value === 'passed' ? '已通过' : display(value)} /> : '-' },
    { title: '更新时间', dataIndex: 'updatedAt', width: 175, render: value => formatDateTime(value, '-') },
    {
      title: '操作',
      width: 100,
      render: (_, rule) => rule.status === 'draft'
        ? <Button type="link" onClick={() => confirmRule(rule)}>确认启用</Button>
        : <span className="aic-muted">只读</span>
    }
  ]

  const renderDelayEditor = () => <div className="aic-rule-form-grid">
    <div className="aic-form-field aic-form-field-wide">
      <label htmlFor="aic-delay-description">判定规则</label>
      <Input.TextArea
        id="aic-delay-description"
        rows={4}
        value={drafts.delay.description}
        onChange={event => updateDraft({ description: event.target.value })}
        placeholder="说明何时判定为数据断更"
        maxLength={1000}
        showCount
      />
    </div>
    <div className="aic-form-field">
      <span id="aic-delay-timezone-label">时区</span>
      <Select aria-labelledby="aic-delay-timezone-label" value={drafts.delay.timezone} onChange={value => updateDraft({ timezone: value })} options={[{ value: 'Asia/Shanghai', label: 'Asia/Shanghai' }]} />
    </div>
    <div className="aic-form-field">
      <label htmlFor="aic-delay-grace-hours">宽限小时数</label>
      <Input id="aic-delay-grace-hours" type="number" min={0} value={drafts.delay.gracePeriodHours} onChange={event => updateDraft({ gracePeriodHours: event.target.value })} />
    </div>
  </div>

  const renderAiEditor = type => {
    const draft = drafts[type]
    const isCoverage = type === 'coverage'
    return <>
      {fields.length === 0
        ? <Alert showIcon type="info" message="该数据源还没有字段元数据，暂时不能配置 AI 规则" />
        : <div className="aic-rule-form-grid">
          <div className="aic-form-field">
            <span id={`aic-${type}-table-label`}>逻辑表</span>
            <Select
              aria-labelledby={`aic-${type}-table-label`}
              value={draft.tableId}
              onChange={value => updateDraft({ tableId: value, fieldName: undefined })}
              options={tables}
              placeholder="请选择逻辑表"
            />
          </div>
          <div className="aic-form-field">
            <span id={`aic-${type}-field-label`}>{isCoverage ? '数据日期字段' : '校验字段'}</span>
            <Select
              aria-labelledby={`aic-${type}-field-label`}
              showSearch
              optionFilterProp="label"
              value={draft.fieldName}
              onChange={value => updateDraft({ fieldName: value })}
              options={activeFields.map(field => ({ value: field.fieldName, label: `${field.fieldName}${field.displayName ? ` · ${field.displayName}` : ''}` }))}
              placeholder="请选择字段"
            />
          </div>
          <div className="aic-form-field">
            <span id={`aic-${type}-option-label`}>{isCoverage ? '日期粒度' : '异常等级'}</span>
            {isCoverage
              ? <Select aria-labelledby={`aic-${type}-option-label`} value={draft.granularity} onChange={value => updateDraft({ granularity: value })} options={[{ value: 'day', label: '日' }, { value: 'week', label: '周' }, { value: 'month', label: '月' }, { value: 'quarter', label: '季度' }]} />
              : <Select aria-labelledby={`aic-${type}-option-label`} value={draft.severity} onChange={value => updateDraft({ severity: value })} options={[{ value: 'info', label: '提示' }, { value: 'warning', label: '预警' }, { value: 'critical', label: '严重' }]} />}
          </div>
          <div className="aic-form-field aic-form-field-wide">
            <label htmlFor={`aic-${type}-description`}>{isCoverage ? '最新数据截止日期规则说明' : '字段缺失或报错规则说明'}</label>
            <Input.TextArea
              id={`aic-${type}-description`}
              rows={5}
              value={draft.description}
              onChange={event => updateDraft({ description: event.target.value })}
              placeholder={isCoverage ? '例如：按自然日统计，查询订单日期字段中的最大日期' : '例如：订单号不能为空，空字符串也视为缺失'}
              maxLength={2000}
              showCount
            />
          </div>
        </div>}
      <div className="aic-rule-actions">
        <Button type="primary" icon={<RobotOutlined />} loading={generating} disabled={fields.length === 0} onClick={generateCandidate}>生成 AI 候选</Button>
        <span className="aic-muted">AI 只生成候选，服务端安全校验和人工确认后才能启用。</span>
      </div>
      <CandidatePreview candidate={candidate} />
    </>
  }

  return <div>
    <Nav active="settings" />
    <div className="aic-page-header aic-settings-header">
      <Typography.Title level={1}>可靠性规则</Typography.Title>
      <Typography.Paragraph type="secondary">配置数据截止日期、数据断更和字段质量判定。规则先保存为草稿，人工确认后启用。</Typography.Paragraph>
    </div>
    <Card className="aic-panel aic-dataset-picker">
      <div>
        <div className="aic-section-eyebrow">配置对象</div>
        <Typography.Title level={3}>{selectedDataset ? selectedDataset.projectName : '选择数据源'}</Typography.Title>
        <div className="aic-muted">仅展示当前账号有权查看的数据源；规则确认仍由后端校验 Owner 权限。</div>
      </div>
      <Select
        aria-label="选择要配置可靠性规则的数据源"
        showSearch
        optionFilterProp="label"
        value={datasetId}
        onChange={setDatasetId}
        placeholder="请选择数据源"
        options={datasets.map(item => ({ value: item.datasetId, label: item.projectName || `数据源 #${item.datasetId}` }))}
      />
    </Card>
    {error && <Alert className="aic-settings-alert" showIcon type="error" message={error} />}
    {!datasetId
      ? <Card className="aic-panel"><Empty description="当前没有可配置的数据源" /></Card>
      : <Spin spinning={loading}>
        <div className="aic-settings-grid">
          <Card className="aic-panel aic-rule-editor" title={<span><SafetyCertificateOutlined /> 规则配置</span>}>
            <Tabs activeKey={activeType} onChange={value => { setActiveType(value); setCandidate(null) }}>
              <Tabs.TabPane tab="数据截止日期" key="coverage" />
              <Tabs.TabPane tab="字段质量" key="quality" />
              <Tabs.TabPane tab="导入截止" key="delay" />
            </Tabs>
            {activeType === 'delay' ? renderDelayEditor() : renderAiEditor(activeType)}
            <div className="aic-save-bar">
              <div><CheckCircleOutlined /> 保存后仍是草稿，不会立即影响健康度或产生异常。</div>
              <Button loading={saving} onClick={saveDraft}>保存草稿</Button>
            </div>
          </Card>
          <Card className="aic-panel aic-rule-history" title={`${RULE_LABELS[activeType]}版本`}>
            <Table
              size="small"
              rowKey={rule => rule.id || `${activeType}-${rule.version}`}
              columns={ruleColumns}
              dataSource={rules[activeType] || []}
              pagination={false}
              scroll={{ x: 760 }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无规则版本" /> }}
            />
          </Card>
        </div>
      </Spin>}
  </div>
}
